/**
 * Cross-entity content links (news + albums). A post or album belongs to exactly
 * one owner entity; the owner may tag strictly lower-level entities, and a
 * strictly higher-level entity may share the content onto its own list.
 * Clubs are deliberately outside this hierarchy.
 */

/** Visibility a tagged entity controls for content tagged onto it. */
export const ContentLinkVisibilityValues = ["public", "hidden"] as const;
export type ContentLinkVisibility = (typeof ContentLinkVisibilityValues)[number];

/** Entity kinds that can own, be tagged by, or share content. */
export const ContentEntityTypeValues = ["platform", "federation", "league", "tournament"] as const;
export type ContentEntityType = (typeof ContentEntityTypeValues)[number];

/** How a listed item reaches the entity whose list is being viewed. */
export const ContentRelationValues = ["owned", "tagged", "shared"] as const;
export type ContentRelation = (typeof ContentRelationValues)[number];

/** Link kinds stored between a content item and an entity. */
export const ContentLinkKindValues = ["tagged", "shared"] as const;
export type ContentLinkKind = (typeof ContentLinkKindValues)[number];

/** A single tag/share association surfaced on the news or album detail. */
export type ContentLinkRef = {
  kind: ContentLinkKind;
  entityType: ContentEntityType;
  /** Opaque publicId of the linked entity (`plt_…` / `fed_…` / `lge_…` / `trn_…`). */
  entityId: string;
  title: string | null;
  logo: string | null;
  visibility: ContentLinkVisibility;
};

/** The entity a list request is scoped to; defines what owned/tagged/shared mean. */
export type ContentScopeRef = {
  type: ContentEntityType;
  /** Opaque publicId of the scope entity. */
  id: string;
};

/**
 * Per-entity settings governing what other entities may tag onto this one, and
 * how visible a fresh tag is. Two independent content kinds — news and
 * galleries (albums) — each with a "may others tag this kind onto me at all"
 * switch and the default visibility stamped on a new tag of that kind.
 *
 * Carried flat on the league and organization domain types; changing a default
 * never rewrites existing links (see docs/content-tagging.md).
 */
export type TaggedContentSettings = {
  /** Whether other entities may tag their news onto this entity. */
  allowNewsTagging: boolean;
  /** Visibility stamped on a new news tag pointing at this entity. */
  defaultTaggedNewsVisibility: ContentLinkVisibility;
  /** Whether other entities may tag their galleries (albums) onto this entity. */
  allowGalleryTagging: boolean;
  /** Visibility stamped on a new gallery tag pointing at this entity. */
  defaultTaggedGalleryVisibility: ContentLinkVisibility;
};
